// İSG uzmanı portalı — İş İzni Tipi oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §22.1: `POST /permit-types` (create) / `PUT /permit-types/{uuid}` (update). Alanlar: ad (zorunlu),
// açıklama (opsiyonel), tehlike sınıfı (opsiyonel 1-3 — tip düzeyi varsayılan), **istenen belgeler**
// (§27 belge türü kaydından `DocumentTypeRef[]` çoklu seçim — personel + araç; requiredDocuments
// **replace**), ve düzenlemede **aktif/pasif** (pasif tip yeni/güncellenen izinde seçilemez — §14).
//
// Düzenlemede istenen belge seti `GET /permit-types/{uuid}` (detay) ile yüklenir; kaydetme tüm seti
// gönderir (backend eski seti soft-delete edip yenisini yazar — §22.1 replace). Tipin SAKLADIĞI pasif
// (ya da kayıtta olmayan) bir tür görünür kalır ("(pasif)") ve değişmeden geri gönderilir: sunucu onu
// korur, `INACTIVE` yalnız YENİ eklenen pasif türe döner (§22). Yeni eklenen bir tür reddedilirse
// (`INVALID`/`INACTIVE`, alan `requiredDocuments[n].code`) kayıt tazelenir, o tür seçimden çıkarılır ve
// grup altında adıyla bildirilir — aynı gövde yeniden gönderilmez (§27.6).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  type DocumentTypeRef,
  Field,
  FormSection,
  type HazardClass,
  Input,
  Modal,
  type PermitTypeDto,
  Select,
  type SelectOption,
  Spinner,
  controlItemsApi,
  documentTypeRefKey,
  documentTypesKey,
  isApiError,
  permitTypesApi,
  protectiveEquipmentApi,
  requiredDocumentRejectionIndex,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { controlItemsKeys } from '../../lib/controlItemsKeys';
import { permitTypesKeys } from '../../lib/permitTypesKeys';
import { protectiveEquipmentKeys } from '../../lib/protectiveEquipmentKeys';
import { RequiredDocumentsPicker } from './RequiredDocumentsPicker';

/** Tehlike sınıfı seçenekleri (opsiyonel — tip düzeyi varsayılan; 1-3; §22). */
const HAZARD_CLASSES = [1, 2, 3] as const;

/** KKD + kontrol katalog seçicilerini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const CATALOG_PAGE_SIZE = 200;

interface PermitTypeFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  permitType?: PermitTypeDto | null;
  onSuccess?: (message: string) => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alanlar backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

/**
 * Sunucunun reddettiği istenen belgeler (`INVALID`/`INACTIVE`). Alan yolundaki sıfır tabanlı dizin
 * GÖNDERİLEN diziye işaret eder (§22, §27.0(9)); böyle bir hata yoksa boş döner.
 */
function rejectedRequiredDocuments(
  error: unknown,
  sent: readonly DocumentTypeRef[],
): DocumentTypeRef[] {
  if (!isApiError(error)) {
    return [];
  }
  const rejected = new Map<string, DocumentTypeRef>();
  for (const e of error.errors) {
    const index = requiredDocumentRejectionIndex(e);
    const ref = index === null ? undefined : sent[index];
    if (ref) {
      rejected.set(documentTypeRefKey(ref), ref);
    }
  }
  return [...rejected.values()];
}

/**
 * Hatada istenen belge reddi DIŞINDA bir şey var mı (ör. sunucu `name`/`hazardClass` hatası ya da
 * alan taşımayan bir zarf hatası) — varsa genel Alert ret satırıyla birlikte gösterilir.
 */
function hasErrorsBesidesRejection(error: unknown): boolean {
  return (
    !isApiError(error) ||
    error.errors.length === 0 ||
    error.errors.some((e) => requiredDocumentRejectionIndex(e) === null)
  );
}

export function PermitTypeFormModal({
  open,
  onOpenChange,
  permitType,
  onSuccess,
}: PermitTypeFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(permitType);

  // Seçili istenen belge türleri — form dışı yerel durum (çoklu seçim), `entity:code` anahtarlı.
  // Düzenlemede detay ucundan (istenen belge seti) yüklenir; oluşturmada boş başlar.
  const [selectedDocs, setSelectedDocs] = useState<Map<string, DocumentTypeRef>>(new Map());
  // Son kaydetmede sunucunun reddettiği (ve seçimden çıkarılan) türler — grup altındaki hata.
  const [rejectedDocs, setRejectedDocs] = useState<DocumentTypeRef[]>([]);
  const requiredDocsRef = useRef<HTMLDivElement>(null);
  // Seçili KKD + kontrol itemı uuid'leri (§24.2/§25.2; replace — boş = temizle). Katalogdan çekilir;
  // düzenlemede detay ucundan (requiredProtectiveEquipment/controlItems) uuid bazlı önyüklenir.
  const [selectedPpe, setSelectedPpe] = useState<Set<string>>(new Set());
  const [selectedControls, setSelectedControls] = useState<Set<string>>(new Set());
  const [isActive, setIsActive] = useState(true);

  // KKD + kontrol katalogları (tip seçicileri) — aktif+pasif tüm (silinmemiş) öge; atanmış-ama-pasif
  // öge de seçili görünsün diye isActive filtresi verilmez ("(pasif)" ipucu ile işaretlenir).
  const ppeCatalogQuery = useQuery({
    queryKey: protectiveEquipmentKeys.options(),
    queryFn: () =>
      protectiveEquipmentApi.listProtectiveEquipment({ page: 1, pageSize: CATALOG_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });
  const controlCatalogQuery = useQuery({
    queryKey: controlItemsKeys.options(),
    queryFn: () => controlItemsApi.listControlItems({ page: 1, pageSize: CATALOG_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });

  // Düzenlemede istenen belge setini + güncel tip alanlarını detaydan çek (liste DTO'sunda
  // istenen belge listesi yoktur; §22 detay taşır).
  const detailQuery = useQuery({
    queryKey: permitType ? permitTypesKeys.detail(permitType.uuid) : permitTypesKeys.all(),
    queryFn: () => permitTypesApi.getPermitType(permitType?.uuid ?? ''),
    enabled: open && isEdit && Boolean(permitType?.uuid),
    staleTime: 30_000,
  });

  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(1, t('permitTypes.validation.nameRequired')),
        description: z.string().trim().optional(),
        // Select yalnız '' | '1' | '2' | '3' sunar; '' → tip düzeyi belirtilmedi (null).
        hazardClass: z.enum(['', '1', '2', '3']),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const detail = detailQuery.data;
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      name: detail?.name ?? permitType?.name ?? '',
      description: detail?.description ?? permitType?.description ?? '',
      hazardClass: (detail?.hazardClass ?? permitType?.hazardClass ?? '').toString() as
        | ''
        | '1'
        | '2'
        | '3',
    },
  });

  // Detay yüklenince (düzenleme) istenen belge setini + aktiflik durumunu seed et;
  // oluşturma modunda boş/aktif başlat. `open` kapanınca temizlenir.
  useEffect(() => {
    if (!open) {
      return;
    }
    setRejectedDocs([]);
    if (isEdit && detail) {
      // Saklı set olduğu gibi (pasif/bilinmeyen kodlar dahil) — değişmeden geri gönderilir (§22).
      setSelectedDocs(new Map(detail.requiredDocuments.map((d) => [documentTypeRefKey(d), d])));
      // KKD + kontrol seçili setini detaydan uuid bazlı önyükle (§24.2/§25.2).
      setSelectedPpe(new Set(detail.requiredProtectiveEquipment.map((p) => p.uuid)));
      setSelectedControls(new Set(detail.controlItems.map((c) => c.uuid)));
      setIsActive(detail.isActive);
    } else if (!isEdit) {
      setSelectedDocs(new Map());
      setSelectedPpe(new Set());
      setSelectedControls(new Set());
      setIsActive(true);
    }
  }, [open, isEdit, detail]);

  // Tehlike sınıfı seçenekleri (§22): somut İSG sınıfları + **"Diğer"** (value '' → `null` gönderilir;
  // tip sınıfı sabitlemez, izin açılışında elle sorulur — §14.1). "Diğer" en sona konur.
  const hazardOptions: SelectOption[] = useMemo(
    () => [
      ...HAZARD_CLASSES.map((h) => ({ value: String(h), label: t(`hazard.${h}`) })),
      { value: '', label: t('permitTypes.form.hazardNone') },
    ],
    [t],
  );

  const mutation = useMutation({
    // Gönderilen dizi değişkenlerde taşınır: sunucunun `requiredDocuments[n]` yolu buna işaret eder.
    mutationFn: ({
      values,
      requiredDocuments,
    }: {
      values: FormValues;
      requiredDocuments: DocumentTypeRef[];
    }) => {
      const hazardClass =
        values.hazardClass === '' ? null : (Number(values.hazardClass) as HazardClass);
      const base = {
        name: values.name.trim(),
        description: nullable(values.description),
        hazardClass,
        requiredDocuments,
        // KKD + kontrol setleri replace (§24.2/§25.2; boş dizi = temizle).
        protectiveEquipmentUuids: [...selectedPpe],
        controlItemUuids: [...selectedControls],
      };
      if (permitType) {
        return permitTypesApi.updatePermitType(permitType.uuid, { ...base, isActive });
      }
      return permitTypesApi.createPermitType(base);
    },
    onError: (error, { requiredDocuments }) => {
      const rejected = rejectedRequiredDocuments(error, requiredDocuments);
      if (rejected.length === 0) {
        return;
      }
      // Kayıt tazelenir (açık seçicinin gözlemcisi yeniden getirir); reddedilen tür listeden düşer.
      void queryClient.invalidateQueries({ queryKey: documentTypesKey });
      setSelectedDocs((prev) => {
        const next = new Map(prev);
        for (const ref of rejected) {
          next.delete(documentTypeRefKey(ref));
        }
        return next;
      });
      setRejectedDocs(rejected);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitTypesKeys.list() });
      queryClient.invalidateQueries({ queryKey: permitTypesKeys.access() });
      queryClient.invalidateQueries({ queryKey: permitTypesKeys.options() });
      if (permitType) {
        queryClient.invalidateQueries({ queryKey: permitTypesKeys.detail(permitType.uuid) });
      }
      reset();
      onSuccess?.(permitType ? t('permitTypes.list.updated') : t('permitTypes.list.created'));
      onOpenChange(false);
    },
  });

  // Ret sonrası odak istenen belgeler grubuna gider (hata orada; Kaydet altbilgide kalır).
  useEffect(() => {
    if (rejectedDocs.length > 0) {
      requiredDocsRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]')?.focus();
    }
  }, [rejectedDocs]);

  function toggleDoc(ref: DocumentTypeRef) {
    setSelectedDocs((prev) => {
      const next = new Map(prev);
      const key = documentTypeRefKey(ref);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, ref);
      }
      return next;
    });
  }

  function toggleInSet(setter: typeof setSelectedPpe, uuid: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  const ppeCatalog = ppeCatalogQuery.data?.items ?? [];
  const controlCatalog = controlCatalogQuery.data?.items ?? [];

  const loadingDetail = isEdit && detailQuery.isPending;
  const typeRejected = rejectedDocs.length > 0;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
          setRejectedDocs([]);
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('permitTypes.form.edit.title') : t('permitTypes.form.create.title')}
      description={t('permitTypes.form.description')}
      size="lg"
      closeLabel={t('permitTypes.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permitTypes.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="permit-type-form"
            loading={mutation.isPending}
            disabled={loadingDetail}
          >
            {mutation.isPending ? (
              <Spinner label={t('permitTypes.common.save')} />
            ) : isEdit ? (
              t('permitTypes.common.save')
            ) : (
              t('permitTypes.common.create')
            )}
          </Button>
        </>
      }
    >
      {loadingDetail ? (
        <div className="flex min-h-[8rem] items-center justify-center">
          <Spinner label={t('permitTypes.form.loading')} />
        </div>
      ) : (
        <form
          id="permit-type-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit((values) => {
            setRejectedDocs([]);
            mutation.mutate({ values, requiredDocuments: [...selectedDocs.values()] });
          })}
          noValidate
        >
          {/* Tür reddi grubun altında alan hatası olarak gösterilir; genel Alert yalnız başka bir
              hata da varsa (ör. sunucu `name` hatası) kalır. */}
          {mutation.isError && (!typeRejected || hasErrorsBesidesRejection(mutation.error)) ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              htmlFor="permit-type-name"
              label={t('permitTypes.form.field.name')}
              error={formState.errors.name?.message}
              required
            >
              <Input
                id="permit-type-name"
                invalid={Boolean(formState.errors.name)}
                {...register('name')}
              />
            </Field>
            <Field htmlFor="permit-type-hazard" label={t('permitTypes.form.field.hazardClass')}>
              <Select
                id="permit-type-hazard"
                options={hazardOptions}
                {...register('hazardClass')}
              />
              <p className="mt-1 text-xs text-muted">{t('permitTypes.form.hazardHint')}</p>
            </Field>
          </div>

          <Field htmlFor="permit-type-description" label={t('permitTypes.form.field.description')}>
            <Input id="permit-type-description" {...register('description')} />
          </Field>

          {/* İstenen belgeler — §27 kaydından iki grup (personel, araç; §22 `DocumentTypeRef`, replace). */}
          <FormSection legend={t('permitTypes.form.field.requiredDocuments')}>
            <p className="text-xs text-muted">{t('permitTypes.form.requiredDocsNote')}</p>
            <RequiredDocumentsPicker
              stored={detail?.requiredDocuments ?? []}
              selected={selectedDocs}
              onToggle={toggleDoc}
              rejected={rejectedDocs}
              containerRef={requiredDocsRef}
            />
          </FormSection>

          {/* İstenen KKD (§24.2; replace — boş = temizle). Katalogdan çoklu seçim; pasif öge "(pasif)"
              ipucu ile de seçili kalabilir (atanmış-ama-pasif düşmesin). Katalog boşsa yönlendirme notu. */}
          <FormSection legend={t('permitTypes.form.field.protectiveEquipment')} className="gap-2">
            <p className="text-xs text-muted">{t('permitTypes.form.protectiveEquipmentNote')}</p>
            {ppeCatalogQuery.isPending ? (
              <Spinner label={t('permitTypes.common.loading')} />
            ) : ppeCatalog.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted">
                {t('permitTypes.form.protectiveEquipmentEmpty')}
              </p>
            ) : (
              <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
                {ppeCatalog.map((item) => (
                  <label
                    key={item.uuid}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={selectedPpe.has(item.uuid)}
                      onChange={() => toggleInSet(setSelectedPpe, item.uuid)}
                    />
                    <span className="font-medium">{item.name}</span>
                    {!item.isActive ? (
                      <span className="text-xs text-muted">
                        {t('permitTypes.catalog.inactiveSuffix')}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
            )}
          </FormSection>

          {/* Bağlı kontrol itemları (§25.2; replace — boş = temizle). Katalogdan çoklu seçim. */}
          <FormSection legend={t('permitTypes.form.field.controlItems')} className="gap-2">
            <p className="text-xs text-muted">{t('permitTypes.form.controlItemsNote')}</p>
            {controlCatalogQuery.isPending ? (
              <Spinner label={t('permitTypes.common.loading')} />
            ) : controlCatalog.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted">
                {t('permitTypes.form.controlItemsEmpty')}
              </p>
            ) : (
              <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
                {controlCatalog.map((item) => (
                  <label
                    key={item.uuid}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={selectedControls.has(item.uuid)}
                      onChange={() => toggleInSet(setSelectedControls, item.uuid)}
                    />
                    <span className="font-medium">{item.text}</span>
                    {!item.isActive ? (
                      <span className="text-xs text-muted">
                        {t('permitTypes.catalog.inactiveSuffix')}
                      </span>
                    ) : null}
                  </label>
                ))}
              </div>
            )}
          </FormSection>

          {/* Aktif/pasif — yalnız düzenlemede (yeni tip aktif başlar; §22). */}
          {isEdit ? (
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="flex flex-col">
                <span className="font-medium">{t('permitTypes.form.field.isActive')}</span>
                <span className="text-xs text-muted">{t('permitTypes.form.isActiveHint')}</span>
              </span>
            </label>
          ) : null}
        </form>
      )}
    </Modal>
  );
}
